import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { ParteSelector } from "./CrediusParti";
import { toast } from "sonner";
import { X, Save } from "lucide-react";

const SEL = "h-11 w-full border-2 border-slate-300 rounded-lg px-3 bg-white mt-1";

export function CrediusModificaPratica({ pratica: p, meta, onSaved, onClose }) {
  const [form, setForm] = useState({
    oggetto: p.oggetto || "", descrizione: p.descrizione || "",
    settore: p.settore || "", avvocato_fornitore_id: p.avvocato_fornitore_id || "",
  });
  const [clienti, setClienti] = useState(p.clienti || []);
  const [controparti, setControparti] = useState(p.controparti || []);
  const [condomini, setCondomini] = useState([]);
  const [residenti, setResidenti] = useState([]);
  const [soggetti, setSoggetti] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/condomini"), api.get("/residenti"), api.get("/credius/soggetti")]).then(([c, r, s]) => {
      setCondomini(c.data);
      const byId = Object.fromEntries(c.data.map((x) => [x.id, x.nome]));
      setResidenti(r.data.map((x) => ({ ...x, condominio_nome: byId[x.condominio_id] || "" })));
      setSoggetti(s.data);
    }).catch(() => {});
  }, []);

  const salva = async () => {
    if (!form.oggetto.trim()) { toast.error("Indica l'oggetto della pratica"); return; }
    if (clienti.length === 0) { toast.error("Indica almeno un cliente"); return; }
    setBusy(true);
    try {
      const { data } = await api.put(`/credius/pratiche/${p.id}`, { ...form, clienti, controparti });
      const prima = !p.avvocato_fornitore_id && !!form.avvocato_fornitore_id;
      const riaffido = !!p.avvocato_fornitore_id && form.avvocato_fornitore_id !== p.avvocato_fornitore_id;
      toast.success(
        prima ? "Pratica affidata: l'avvocato è stato avvisato" :
        riaffido ? "Pratica riaffidata: il nuovo avvocato è stato avvisato" :
        "Pratica aggiornata"
      );
      onSaved(data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Salvataggio non riuscito"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="credius-modifica-dialog">
      <div className="bg-white rounded-xl max-w-2xl w-full p-5 max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-bold text-lg text-indigo-950">Modifica pratica {p.numero}</h2>
          <button type="button" data-testid="credius-modifica-chiudi" onClick={onClose}><X className="w-5 h-5 text-slate-500" /></button>
        </div>
        <div className="space-y-4">
          <div>
            <Label>Oggetto pratica *</Label>
            <Input data-testid="modifica-oggetto" value={form.oggetto}
              onChange={(e) => setForm({ ...form, oggetto: e.target.value })} className="h-11 mt-1" />
          </div>
          <div>
            <Label>Breve descrizione della controversia</Label>
            <Textarea data-testid="modifica-descrizione" rows={3} value={form.descrizione}
              onChange={(e) => setForm({ ...form, descrizione: e.target.value })} className="mt-1" />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label>Avvocato affidatario</Label>
              <select data-testid="modifica-avvocato" value={form.avvocato_fornitore_id}
                onChange={(e) => setForm({ ...form, avvocato_fornitore_id: e.target.value })} className={SEL}>
                <option value="">— Non ancora assegnato —</option>
                {(meta.avvocati || []).map((a) => <option key={a.id} value={a.id}>{a.ragione_sociale}</option>)}
              </select>
              {p.avvocato_fornitore_id && form.avvocato_fornitore_id !== p.avvocato_fornitore_id && (
                <p className="text-xs text-amber-700 mt-1">⚠ Cambiando avvocato l&apos;incarico tornerà &laquo;in attesa di accettazione&raquo;.</p>
              )}
              {!p.avvocato_fornitore_id && form.avvocato_fornitore_id && (
                <p className="text-xs text-emerald-700 mt-1">✓ La pratica passerà a &laquo;Affidata&raquo; e l&apos;avvocato riceverà la richiesta di accettazione.</p>
              )}
            </div>
            <div>
              <Label>Settore giuridico</Label>
              <select data-testid="modifica-settore" value={form.settore}
                onChange={(e) => setForm({ ...form, settore: e.target.value })} className={SEL}>
                <option value="">— Seleziona —</option>
                {(meta.settori || []).map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <ParteSelector label="Clienti *" parti={clienti} setParti={setClienti}
            condomini={condomini} residenti={residenti} soggetti={soggetti}
            sources={[
              { key: "condominio", label: "Condominio" },
              { key: "condomino", label: "Condòmino (anagrafica presente)" },
              { key: "soggetto", label: "Altri clienti" },
            ]} testid="modifica-clienti" />
          <ParteSelector label="Controparti" parti={controparti} setParti={setControparti}
            condomini={condomini} residenti={residenti} soggetti={soggetti}
            sources={[
              { key: "condomino", label: "Condòmini già presenti in anagrafica" },
              { key: "soggetto", label: "Altri soggetti" },
              { key: "tutti", label: "Tutti" },
            ]} testid="modifica-controparti" />
          <div className="actions-row flex flex-wrap gap-3 pt-1">
            <Button data-testid="modifica-salva" disabled={busy} onClick={salva}
              className="h-11 bg-indigo-900 hover:bg-indigo-950">
              <Save className="w-4 h-4 mr-1.5" /> Salva modifiche
            </Button>
            <Button variant="outline" className="h-11" onClick={onClose}>Annulla</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
